import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { AdelanteService } from '../../services/adelante.service';
import { DatastoreService } from '../../services/datastore.service';
import { ApiService } from '../../services/jrni/api.service';
import { ServicesService } from '../../services/jrni/services.service';
import { AvailabilityService } from '../../services/jrni/availability.service';
import { DepartmentService } from '../../services/jrni/department.service';
import { QuestionsService } from '../../services/questions.service';
import { AlertService } from '../../_alert';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, timer } from 'rxjs';
import { AppConfig } from '../../app.config';

@Component({
  selector: 'app-verify-payment',
  templateUrl: './verify-payment.component.html',
  styleUrls: ['./verify-payment.component.scss'],
})
export class VerifyPaymentComponent implements OnInit, OnDestroy {
  pageTitle = 'Verifying payment';
  verifyPageText = 'Please wait while we verify your payment with Adelante...';
  webhookResponseStatus = '';
  companyId: string;
  basket: string;
  serviceItem: string;
  failed = false;
  maxRetriesReached = false;
  adelanteStatus = '';
  chargeId = '';
  receipt = '';
  ref = '';
  config: any;
  private pollSub: Subscription;
  private maxRetries = 300;
  private initialDataExtracted = false;
  private selectedLocation: any;
  private selectedSlot: any;
  private findErrorCount = 0;
  private maxFindErrors = 10;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private spinner: NgxSpinnerService,
    private adelanteService: AdelanteService,
    private datastoreService: DatastoreService,
    private apiService: ApiService,
    private servicesService: ServicesService,
    private availabilityService: AvailabilityService,
    private departmentService: DepartmentService,
    private questionsService: QuestionsService,
    private alertService: AlertService,
    private translateService: TranslateService
  ) {}

  async ngOnInit() {
    this.spinner.show();

    this.route.params.subscribe(async (params) => {
      if (params.company) {
        this.companyId = params.company;
      }
      if (params.basket) {
        this.basket = params.basket;
      }
      if (params.serviceItems) {
        // Service items come as comma-separated in the URL, take the first non-empty one
        const items = params.serviceItems.split(',').filter((s) => s);
        if (items.length > 0) {
          this.serviceItem = items[0];
        }
      }

      console.log('Verify payment params — company:', this.companyId, 'basket:', this.basket, 'serviceItem:', this.serviceItem);

      // Load config and resolve location — nothing runs until these complete
      try {
        this.config = await this.apiService.getConfig(AppConfig.settings.core.company_id);
        this.datastoreService.config = this.config;
        console.log('Config loaded:', this.config);
      } catch (err) {
        console.error('Error loading config:', err);
      }

      // Resolve the child location from the parent so confirmation page can find it
      try {
        const parent = await this.departmentService.getParentDepartment();
        const children = await this.departmentService.getChildDepartments(parent);
        for (const location of children) {
          if (location.id == this.companyId) {
            await this.departmentService.setLocation(location);
            this.selectedLocation = location;
            console.log('Location set:', location);
            break;
          }
        }
      } catch (err) {
        console.error('Error resolving location:', err);
      }

      if (this.basket && this.serviceItem) {
        this.startPolling();
      } else {
        console.error('Missing basket or serviceItem from route params');
        this.spinner.hide();
        this.pageTitle = 'Payment verification error';
        this.verifyPageText = 'Missing payment details. Please try again.';
        this.failed = true;
      }
    });
  }

  ngOnDestroy() {
    if (this.pollSub) {
      this.pollSub.unsubscribe();
    }
  }

  private startPolling() {
    // Poll immediately then every 10 seconds
    const source = timer(0, 10000);
    this.pollSub = source.subscribe(async (attempt) => {
      if (attempt >= this.maxRetries) {
        this.pollSub.unsubscribe();
        this.spinner.hide();
        this.maxRetriesReached = true;
        this.pageTitle = 'Payment verification timed out';
        this.verifyPageText =
          'We were unable to verify your payment in time. Please contact us for assistance.';
        return;
      }

      try {
        const result: any = await this.adelanteService
          .checkStatus(this.basket, this.serviceItem)
          .toPromise();
        console.log(`Adelante status check (attempt ${attempt + 1}):`, result);

        // Check top-level status for failures (e.g. FAILED_CHECKOUT_FAILED)
        if (result && result.status && result.status.indexOf('FAILED') !== -1) {
          this.pollSub.unsubscribe();
          this.spinner.hide();
          this.failed = true;
          this.pageTitle = 'Payment unsuccessful';
          this.verifyPageText =
            'Your payment was not authorised. Please try again or use a different payment method.';
          return;
        }

        if (result && result.adelante_status) {
          this.adelanteStatus = result.adelante_status;

          if (result.adelante_status !== 'Awaiting') {
            // Status has changed — stop polling
            this.pollSub.unsubscribe();

            if (result.adelante_status === 'Authorised') {
              this.startFindPolling();
            } else {
              // Declined or any other non-Awaiting status
              this.spinner.hide();
              this.failed = true;
              this.pageTitle = 'Payment unsuccessful';
              this.verifyPageText =
                'Your payment was not authorised. Please try again or use a different payment method.';
            }
          }
        }
      } catch (err) {
        console.error('Error checking Adelante status:', err);
      }
    });
  }

  private startFindPolling() {
    this.verifyPageText = 'Payment authorised. Finalising your booking...';
    // Poll preBasketFind every 2 seconds
    const source = timer(0, 2000);
    this.pollSub = source.subscribe(async (attempt) => {
      if (attempt >= this.maxRetries) {
        this.pollSub.unsubscribe();
        this.spinner.hide();
        this.maxRetriesReached = true;
        this.pageTitle = 'Payment verification timed out';
        this.verifyPageText =
          'We were unable to verify your payment in time. Please contact us for assistance.';
        return;
      }

      try {
        const found: any = await this.adelanteService.preBasketFind(
          this.companyId, this.basket, this.serviceItem
        );
        console.log(`preBasketFind (attempt ${attempt + 1}):`, found);

        if (found && found.data) {
          // Extract initial cached data on first successful find
          if (!this.initialDataExtracted) {
            await this.extractInitialData(found);
            this.initialDataExtracted = true;
          }

          if (found.status) {
            this.webhookResponseStatus = `COMMON.${found.status}`;
          }
          if (found.data.auth_code) {
            this.chargeId = found.data.auth_code;
          }
          if (found.ref) {
            this.ref = found.ref;
          }
          if (found.data.receipt) {
            this.receipt = found.data.receipt;
          }

          // Check for failure during checkout (e.g. FAILED_CHECKOUT_FAILED)
          if (found.status && found.status.indexOf('FAILED') !== -1) {
            this.pollSub.unsubscribe();
            this.spinner.hide();
            this.webhookResponseStatus = '';
            this.failed = true;
            this.pageTitle = 'Payment unsuccessful';
            this.verifyPageText =
              'Your payment was not authorised. Please try again or use a different payment method.';
            return;
          }

          // purchase_id indicates the JRNI booking has been created by the backend webhook
          if (found.data.purchase_id) {
            this.pollSub.unsubscribe();
            await this.hydrateAndNavigate(found);
          }
        }
      } catch (err) {
        console.error('Error in preBasketFind polling:', err);
        this.findErrorCount++;
        if (this.findErrorCount >= this.maxFindErrors) {
          this.pollSub.unsubscribe();
          this.spinner.hide();
          this.pageTitle = 'Payment verification error';
          this.verifyPageText =
            'Unable to verify your booking. Please contact us for assistance.';
          this.failed = true;
        }
      }
    });
  }

  private async extractInitialData(found: any) {
    const data = found.data;
    if (!data) return;

    if (data.extra_items?.lookupResponses) {
      this.datastoreService.lookupResponses = data.extra_items.lookupResponses;
    }
    if (data.extra_items?.selectedAddress) {
      this.datastoreService.selectedAddress = data.extra_items.selectedAddress;
    }

    if (data.auth_token) {
      localStorage.setItem('auth_token', data.auth_token);
    }

    if (data.journey_route) {
      this.datastoreService.journeyRoute = data.journey_route;
      const content = this.config?.journey_config?.appointments?.page_content?.[data.journey_route];
      this.datastoreService.pageContent = content;
    }

    if (data.detail_group) {
      this.getCompanyQuestions(data.detail_group);
    }

    if (data.slot) {
      this.selectedSlot = data.slot;
      this.datastoreService.selectedSlot = data.slot;
      await this.availabilityService.setSelectedSlot(data.slot);
    }
    this.datastoreService.extraDuration = data.extra_duration || 0;

    if (data.extra_items?.fees) {
      this.datastoreService.serviceFees = data.extra_items.fees;
    }
  }

  private async hydrateAndNavigate(found: any) {
    try {
      const data = found.data;
      const location = this.selectedLocation || await this.departmentService.getLocation();

      const purchase: any = await this.apiService.findPurchase(location, data.purchase_id);
      console.log('JRNI purchase:', purchase);
      const booking = purchase._embedded.bookings[0];
      this.datastoreService.booking = booking;

      const service: any = await this.apiService.getService(location, booking.service_id);
      this.datastoreService.selectedService = service;
      await this.servicesService.set(service);

      localStorage.setItem('booking', JSON.stringify(booking));
      localStorage.setItem('date', JSON.stringify(this.selectedSlot));
      localStorage.setItem('location', JSON.stringify(location));
      localStorage.setItem('service', JSON.stringify(service));

      this.spinner.hide();
      this.router.navigate(['confirmation']);
    } catch (err) {
      console.error('Error hydrating confirmation data:', err);
      this.spinner.hide();
      this.pageTitle = 'Payment verification error';
      this.verifyPageText =
        'An error occurred while loading your booking details. Please contact us for assistance.';
      this.failed = true;
    }
  }

  private getCompanyQuestions(detailGroup) {
    this.questionsService.getCompanyQuestions(detailGroup).subscribe((res: any) => {
      const questions = res.questions;
      this.datastoreService.companyQuestions = questions;
    });
  }

  restartJourney() {
    if (sessionStorage.getItem('journey')) {
      this.router.navigate(['start/' + sessionStorage.getItem('journey')]);
    } else {
      this.router.navigate(['/start']);
    }
  }
}
